import { useEffect, useState, type CSSProperties } from 'react';
import { Menu, Phone, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language } from '@/lib/translations';

const headerLayoutStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  padding: '1rem clamp(1rem, 1.7vw, 1.5rem)',
};

const menuButtonStyle: CSSProperties = {
  justifySelf: 'start',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.5rem',
  height: '2.5rem',
};

const headerControlsStyle: CSSProperties = {
  justifySelf: 'end',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
};

const menuBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  background: 'rgba(0, 0, 0, 0.1)',
  color: '#33383C',
};

const menuDrawerStyle: CSSProperties = {
  position: 'absolute',
  top: '0.5rem',
  bottom: '0.5rem',
  left: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  width: 'calc(100vw - 1rem)',
  maxWidth: '390px',
  overflow: 'hidden',
  background: '#FBFAF8',
  boxShadow: '0 24px 70px rgba(0, 0, 0, 0.22)',
};

const drawerCloseStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '3rem',
  color: '#33383C',
};

const drawerTabsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '2.25rem',
  marginTop: '3rem',
  padding: '0 1rem',
  fontSize: '15px',
  lineHeight: 1,
};

const drawerLinksStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  marginTop: '0.75rem',
  padding: '0 1rem',
};

const drawerLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '43px',
  color: '#919191',
  fontSize: '15px',
  textDecoration: 'none',
};

const drawerFooterStyle: CSSProperties = {
  marginTop: 'auto',
  padding: '0 1rem 1.5rem',
  color: '#919191',
  fontSize: '15px',
};

const sectionPathById: Record<string, string> = {
  appointment: '/appointement',
  beforeAfter: '/beforeAfter',
  expertise: '/expertise',
  location: '/location',
  videos: '/videos',
};

const updateUrlForSection = (targetId: string) => {
  const path = sectionPathById[targetId];

  if (!path) {
    return;
  }

  window.history.pushState(null, '', `${path}${window.location.search}`);
};

const scrollToSectionWhenReady = (targetId: string, attempt = 0) => {
  const target = document.getElementById(targetId);

  if (target) {
    updateUrlForSection(targetId);
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY,
      behavior: 'smooth',
    });
    return;
  }

  if (attempt >= 10) {
    return;
  }

  window.setTimeout(() => scrollToSectionWhenReady(targetId, attempt + 1), 150);
};

export function Header() {
  const { language, setLanguage, isRTL } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const languages: { code: Language; label: string }[] = [
    { code: 'fr', label: 'FR' },
    { code: 'ar', label: 'AR' },
    { code: 'en', label: 'EN' },
  ];

  const callUsLabel =
    language === 'fr' ? 'Appelez-nous : 06 62 25 10 47' : language === 'ar' ? 'اتصل بنا: 06 62 25 10 47' : 'Call us: 06 62 25 10 47';

  const menuLabel = language === 'fr' ? 'Menu' : language === 'ar' ? 'القائمة' : 'Menu';
  const closeMenuLabel = language === 'fr' ? 'Fermer le menu' : language === 'ar' ? 'إغلاق القائمة' : 'Close menu';
  const menuPrimaryTab = language === 'fr' ? 'Bloom Clinic' : language === 'ar' ? 'بلوم كلينيك' : 'Bloom Clinic';
  const menuSecondaryTab = language === 'fr' ? 'Contact' : language === 'ar' ? 'تواصل' : 'Contact';
  const menuLinks = [
    { href: '#appointment', label: language === 'fr' ? 'Rendez-vous' : language === 'ar' ? 'موعد' : 'Appointment' },
    { href: '#beforeAfter', label: language === 'fr' ? 'Avant/Après' : language === 'ar' ? 'قبل/بعد' : 'Before/After' },
    { href: '#expertise', label: language === 'fr' ? 'Notre expertise' : language === 'ar' ? 'خبرتنا' : 'Our expertise' },
    { href: '#location', label: language === 'fr' ? 'Adresses' : language === 'ar' ? 'العناوين' : 'Locations' },
  ];

  const handleMenuLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();
    setIsMenuOpen(false);
    const targetId = href.slice(1);

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('bloom:navigate-section', { detail: { targetId } }));
      scrollToSectionWhenReady(targetId);
    }, 150);
  };

  useEffect(() => {
    if (!isMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMenuOpen]);

  return (
    <header className={`sticky top-0 z-50 bg-white shadow-lg border-b border-gray-100 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div style={headerLayoutStyle}>
        <button
          type="button"
          onClick={() => setIsMenuOpen((current) => !current)}
          aria-label={isMenuOpen ? closeMenuLabel : menuLabel}
          aria-expanded={isMenuOpen}
          className="ltr flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-800 transition-all duration-300 hover:scale-110 hover:bg-[#FFE600] hover:text-black"
          style={menuButtonStyle}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Logo and Brand */}
        <a href="/" aria-label="Bloom clinic home" className="flex items-center gap-3 flex-shrink-0 justify-self-center" style={{ justifySelf: 'center' }}>
          <img src="/bloom_clinic_logo.webp" alt="Bloom Clinic Logo" width={192} height={64} className="h-12 w-auto md:h-16" />
        </a>

        {/* Language Selector */}
        <div className="ltr flex items-center gap-2 justify-self-end" style={headerControlsStyle}>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value as Language)}
            aria-label="Language"
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FFE600]"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
          <a
            href="tel:0662251047"
            aria-label={callUsLabel}
            title={callUsLabel}
            className="phone-cta flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-800 transition-all duration-300 hover:scale-110 hover:bg-[#FFE600] hover:text-black"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '2.5rem', height: '2.5rem' }}
          >
            <Phone size={18} />
          </a>
        </div>
      </div>

      {isMenuOpen && (
        <div style={menuBackdropStyle} onClick={() => setIsMenuOpen(false)}>
          <nav
            aria-label={menuLabel}
            style={menuDrawerStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              aria-label={closeMenuLabel}
              style={drawerCloseStyle}
            >
              <X size={18} />
            </button>

            <div style={drawerTabsStyle}>
              <span className="font-medium">{menuPrimaryTab}</span>
              <a href="tel:0662251047" onClick={() => setIsMenuOpen(false)} style={{ color: '#33383C', textDecoration: 'none' }}>
                {menuSecondaryTab}
              </a>
            </div>

            <div style={drawerLinksStyle}>
              {menuLinks.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(event) => handleMenuLinkClick(event, item.href)}
                  style={drawerLinkStyle}
                >
                  <span>{item.label}</span>
                  <span aria-hidden="true" style={{ color: '#B68A2E', fontSize: '18px', lineHeight: 1 }}>›</span>
                </a>
              ))}
            </div>

            <div style={drawerFooterStyle}>
              <a href="tel:0662251047" onClick={() => setIsMenuOpen(false)} style={{ display: 'block', padding: '0.75rem 0', color: '#919191', textDecoration: 'none' }}>
                {callUsLabel}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

